/**
 * The shapes the API speaks.
 *
 * Hand-written for now. They mirror the records in Denetim.Application, and the one thing
 * that keeps them honest is that every enum travels as a name — the same name the database
 * stores and the form snapshot writes. A number here would drift against the C# silently;
 * a name that stops matching fails loudly the first time it is read.
 *
 * When the surface grows past the definition screens these should be generated from the
 * OpenAPI document rather than typed twice.
 */

export type AnswerType = 'YesNo' | 'Scale' | 'SingleChoice' | 'MultipleChoice' | 'Text';

/** One option on a choice question, and what choosing it is worth. */
export interface QuestionOption {
  id: string;
  text: string;
  /**
   * Per cent of the question. A single choice scores this directly; a multiple choice
   * scores the share of the whole list that was ticked.
   */
  score: number;
  order: number;
}

export interface SaveQuestionOption {
  text: string;
  score: number;
}

/** Whether the type offers a list to choose from. Matches AnswerTypes.HasOptions. */
export const hasOptions = (type: AnswerType) =>
  type === 'SingleChoice' || type === 'MultipleChoice';

/** Whether answering it can earn or lose points. Free text cannot. */
export const isScored = (type: AnswerType) => type !== 'Text';
export type CriticalScope = 'None' | 'Category' | 'Form';
export type RequirementTrigger = 'Never' | 'WhenNegative' | 'Always';
export type NotApplicableScope = 'Category' | 'Form';

/**
 * How a form's numbers are meant to be read.
 *
 * The arithmetic is the same either way — a form whose category weights equal the sums of
 * their questions already scores exactly like points out of a hundred. What differs is what
 * the designer is asked for and what the screen calls the column.
 */
export type ScoringModel = 'Weight' | 'Points';

export interface TokenPair {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

export interface QuestionCategory {
  id: string;
  name: string;
  code: string;
  description: string | null;
  /** Shown before offering a delete: a category taken away takes its questions out of reach. */
  questionCount: number;
}

export interface SaveQuestionCategory {
  name: string;
  code: string;
  description: string | null;
}

export interface LibraryQuestion {
  id: string;
  categoryId: string;
  categoryName: string;
  code: string;
  text: string;
  guidance: string | null;
  answerType: AnswerType;
  scaleMaximum: number;
  options: QuestionOption[];
  criticalScope: CriticalScope;
  commentRequirement: RequirementTrigger;
  photoRequirement: RequirementTrigger;
  allowsNotApplicable: boolean;
  notApplicableScope: NotApplicableScope;
  isActive: boolean;
  /** Zero means it can still be deleted. Anything else and it is retired instead. */
  usedInTemplates: number;
}

export interface SaveLibraryQuestion {
  categoryId: string;
  code: string;
  text: string;
  guidance: string | null;
  answerType: AnswerType;
  scaleMaximum: number;
  options: SaveQuestionOption[];
  criticalScope: CriticalScope;
  commentRequirement: RequirementTrigger;
  photoRequirement: RequirementTrigger;
  allowsNotApplicable: boolean;
}

export interface VisitType {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  usedInTemplates: number;
}

export interface SaveVisitType {
  name: string;
  description: string | null;
}

/**
 * The option lists the editor renders. The labels live in the dictionaries now — an enum
 * value is a wire constant, and what it is called depends on who is reading.
 */
export const ANSWER_TYPES: AnswerType[] = [
  'YesNo',
  'Scale',
  'SingleChoice',
  'MultipleChoice',
  'Text',
];
export const CRITICAL_SCOPES: CriticalScope[] = ['None', 'Category', 'Form'];
export const REQUIREMENT_TRIGGERS: RequirementTrigger[] = ['Never', 'WhenNegative', 'Always'];
export const NA_SCOPES: NotApplicableScope[] = ['Category', 'Form'];
